/**
 * ComplianceSettingsPanel — single-knob compliance config (Brief 14c-ii).
 *
 * v0.1 surface: just the MLCO routing threshold (Low / Medium / High).
 * MLCO can view (read-only); only COLP can save changes.
 *
 * Mounts collapsibly at the top of the MLCO Inbox page; 14d moves it to
 * the COLP dashboard.
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Loader2 } from "lucide-react";
import { API } from "../../config/api";

import { errorMessage } from "../../utils/errorMessage";
const THRESHOLDS = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
];

function authHeaders() {
  const email =
    sessionStorage.getItem("bonsai_user_id") ||
    localStorage.getItem("bonsai_user_id") ||
    "";
  return email
    ? { "X-Bonsai-User-Id": email }
    : {};
}

export default function ComplianceSettingsPanel({ userMeta }) {
  const canSee = !!(userMeta?.is_mlco || userMeta?.is_colp || userMeta?.is_director);
  const canEdit = !!(userMeta?.is_colp || userMeta?.is_director);

  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [doc, setDoc] = useState(null);
  const [pending, setPending] = useState("");
  const callerEmail =
    sessionStorage.getItem("bonsai_user_id") ||
    localStorage.getItem("bonsai_user_id") ||
    "";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/compliance/compliance-settings`, {
        headers: authHeaders(),
      });
      setDoc(res.data);
      setPending(res.data?.mlco_routing_threshold || "high");
    } catch (err) {
      console.error("compliance-settings load failed:", err);
      toast.error("Could not load compliance settings.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canSee && expanded && !doc && !loading) {
      load();
    }
  }, [canSee, expanded, doc, loading, load]);

  const save = useCallback(async () => {
    if (!canEdit) return;
    if (!pending) return;
    if (!callerEmail) {
      toast.error("Cannot save — missing user email.");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.put(
        `${API}/compliance/compliance-settings`,
        { mlco_routing_threshold: pending, updated_by_email: callerEmail },
        { headers: authHeaders() }
      );
      setDoc(res.data);
      toast.success("Compliance settings updated.");
    } catch (err) {
      console.error("compliance-settings save failed:", err);
      toast.error(
        errorMessage(err, "Could not save. Try again.")
      );
    } finally {
      setSaving(false);
    }
  }, [canEdit, pending, callerEmail]);

  if (!canSee) return null;

  const lastUpdated = doc?.updated_at
    ? new Date(doc.updated_at).toLocaleString()
    : "never";

  return (
    <div
      className="bg-slate-900/40 border border-slate-700/40 rounded-md mb-4"
      data-testid="compliance-settings-panel"
    >
      <button
        type="button"
        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-200 hover:bg-slate-800/40"
        onClick={() => setExpanded((v) => !v)}
        data-testid="compliance-settings-toggle"
      >
        {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <span className="font-medium">Compliance settings</span>
        <span className="text-xs text-slate-500 ml-auto">
          Threshold: {doc?.mlco_routing_threshold || "high"}
        </span>
      </button>
      {expanded && (
        <div className="px-3 pb-3 pt-1 space-y-3" data-testid="compliance-settings-body">
          {loading && (
            <div className="text-xs text-slate-500 inline-flex items-center gap-1">
              <Loader2 size={12} className="animate-spin" /> Loading…
            </div>
          )}
          {!loading && doc && (
            <>
              <fieldset
                role="radiogroup"
                aria-label="MLCO routing threshold"
                className="space-y-1"
                disabled={!canEdit || saving}
              >
                <legend className="text-xs text-slate-400 mb-1">
                  MLCO routing threshold
                  {!canEdit && (
                    <span className="ml-2 text-[10px] text-slate-500" data-testid="compliance-settings-readonly">
                      (read-only — COLP only)
                    </span>
                  )}
                </legend>
                {THRESHOLDS.map((t) => (
                  <label
                    key={t.value}
                    className="flex items-center gap-2 text-xs text-slate-200 cursor-pointer"
                  >
                    <input
                      type="radio"
                      name="mlco-routing-threshold"
                      value={t.value}
                      checked={pending === t.value}
                      onChange={() => setPending(t.value)}
                      disabled={!canEdit || saving}
                      data-testid={`compliance-settings-threshold-${t.value}`}
                    />
                    {t.label}
                  </label>
                ))}
              </fieldset>
              {canEdit && (
                <button
                  type="button"
                  disabled={saving || pending === doc.mlco_routing_threshold}
                  onClick={save}
                  className="px-3 py-1.5 rounded-md text-xs font-medium bg-blue-700/40 text-blue-100 border border-blue-700/60 hover:bg-blue-700/60 disabled:opacity-50"
                  data-testid="compliance-settings-save-btn"
                >
                  {saving ? "Saving…" : "Save"}
                </button>
              )}
              <div
                className="text-[10px] text-slate-500 pt-1"
                data-testid="compliance-settings-meta"
              >
                Last updated by {doc.updated_by || "—"} at {lastUpdated}
                {typeof doc.version === "number" ? ` · v${doc.version}` : ""}
                {doc.is_default ? " · (using code default)" : ""}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
